/**
 * Representação canônica estável + checksum SHA-256 (crypto.subtle no browser).
 */

export const DANGEROUS_KEYS = ["__proto__", "constructor", "prototype"] as const;

/** JSON canônico: chaves ordenadas recursivamente, sem espaços. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalize(value: any): any {
  if (value === null || typeof value !== "object") return value === undefined ? null : value;
  if (Array.isArray(value)) return value.map(canonicalize);
  const out: Record<string, any> = {};
  for (const key of Object.keys(value).sort()) {
    if ((DANGEROUS_KEYS as readonly string[]).includes(key)) continue;
    out[key] = canonicalize(value[key]);
  }
  return out;
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** SHA-256 hex de uma string. Retorna null se WebCrypto não estiver disponível. */
export async function sha256Hex(text: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const bytes = new TextEncoder().encode(text);
  return toHex(await subtle.digest("SHA-256", bytes));
}

/** Hash do payload em sua forma canônica. */
export async function payloadHash(payload: unknown): Promise<string | null> {
  return sha256Hex(canonicalJson(payload));
}

/** Rejeita estruturas com chaves de protótipo em qualquer profundidade. */
export function assertSafeStructure(value: unknown, maxDepth = 24, depth = 0): void {
  if (depth > maxDepth) throw new Error("Estrutura do arquivo é profunda demais (possível arquivo malicioso).");
  if (!value || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const item of value) assertSafeStructure(item, maxDepth, depth + 1);
    return;
  }
  for (const key of Object.keys(value)) {
    if ((DANGEROUS_KEYS as readonly string[]).includes(key)) {
      throw new Error(`Arquivo contém chave proibida "${key}" e foi rejeitado por segurança.`);
    }
    assertSafeStructure((value as any)[key], maxDepth, depth + 1);
  }
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
